import { Plugin } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';

import { KeyboardEventCode } from '@core/common-legacy/keyboard';

import type { MentionServiceInternal } from './mentions-service-internal';
import { MentionsServicePublic } from './mentions-service-public';
import { MentionServiceInternalDefault } from './strategies/mention-service-internal-default';
import { MentionServiceInternalGrouped } from './strategies/mention-service-internal-grouped';
import type {
  MentionElement,
  MentionElementGroup,
  MentionListController,
  MentionNavigationKey,
  MentionsPluginEvents,
} from './types';
import { MentionInternalStrategy } from './types';
import { EditorMode } from '../../editor/types';
import type { EditorButton, RtPMPlugin } from '../../editor/types';

const mentionRegex = /(^@|\s@)(?!.*\1).*/;
const MentionStrategyMap = {
  [MentionInternalStrategy.Default]: MentionServiceInternalDefault,
  [MentionInternalStrategy.Grouped]: MentionServiceInternalGrouped,
};

export class MentionsPlugin implements RtPMPlugin {
  public api: { mentionsService: MentionsServicePublic };
  public buttons = {
    toggleMentionList: <EditorButton>{
      icon: 'alternate-email',
      tooltipText: 'wysiwyg.mentions',
      isActive: () => this.mentionListController.isOpen(),
      run: (state, dispatch, origin) => {
        this.api.mentionsService.mentionsWithSearchOpen(origin);
      },
    },
  };
  public eventStream$ = new Subject<MentionsPluginEvents>();

  private destroy$ = new Subject<void>();
  private destroyed$ = new BehaviorSubject<boolean>(false);
  private editorView: EditorView;
  private mentionServiceInternal: MentionServiceInternal;

  private editorPlugin = new Plugin({
    props: {
      handleDOMEvents: {
        keyup: (view, event) => {
          const { key } = event;
          const keyCodeNavigate =
            KeyboardEventCode.Enter === key ||
            KeyboardEventCode.ArrowUp === key ||
            KeyboardEventCode.ArrowDown === key;
          if (!keyCodeNavigate) {
            const { state } = view;
            const { $to } = state.selection;
            const text = $to.parent.textBetween(0, $to.parentOffset, '\n', '\n');
            const match = mentionRegex.exec(text);
            this.mentionServiceInternal.mentionsMatchHandler(
              view.state,
              match,
              match ? $to.pos - match[0].length : 0,
              match ? $to.pos : 0,
            );
          }
          return false;
        },
      },
      handleKeyDown: (view, event) => {
        const { key } = event;
        const keyCodeNavigate =
          KeyboardEventCode.Enter === key ||
          KeyboardEventCode.ArrowUp === key ||
          KeyboardEventCode.ArrowDown === key;

        if (keyCodeNavigate && this.mentionListController.isOpen()) {
          this.mentionListController.handleKey(<MentionNavigationKey>key);
          return true;
        }

        return false;
      },
    },
  });

  constructor(
    private dataSource: Observable<MentionElement[] | MentionElementGroup[]>,
    private mentionListController: MentionListController,
    private mentionInternalStrategy: MentionInternalStrategy,
    private scroll$: Observable<void>,
  ) {}

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;

    if (EditorMode.Wysiwyg === mode) {
      const service = MentionStrategyMap[this.mentionInternalStrategy];
      this.mentionServiceInternal = new service(
        editorView,
        <any>this.dataSource,
        this.eventStream$,
        this.scroll$,
        this.destroy$,
        this.mentionListController,
      );
      this.api = {
        mentionsService: new MentionsServicePublic(editorView, this.mentionServiceInternal),
      };
    }
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public getNativeEditorPlugins() {
    return [this.editorPlugin];
  }
}
